import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';

import { EmptyAllPages } from './WorkspaceEmptyPages';

import {
  pagesModel,
  DeletePageModal,
  MoveToFolderModal,
  useCreatePage,
} from '../../../features/pages';

import { useTeamPermissions } from '../../../features/teams';

import { useFilteredPages, usePageActions, useView } from '../hooks';

import { WorkspaceFilters } from './WorkspaceFilters';
import { WorkspaceShareModal } from './WorkspaceShareModal';
import { WorkspaceQuickButton } from './WorkspaceQuickButton';

import { PagesList, PagesGrid } from './WorkspacePagesView';

import { QUICK_BUTTON_ITEMS } from '../config';

export const WorkspaceAllPages: React.FC = () => {
  const { view } = useView();

  const { handleCreateNewPage } = useCreatePage();

  const {
    currentPage,
    isDeletePageModalOpen,
    isMovePageToFolderModalOpen,
    isSharePageModalOpen,
    onDeletePageOpen,
    onDeletePageClose,
    onMoveToFolderOpen,
    onMoveToFolderClose,
    handleSharePage,
    handleSharePageClose,
  } = usePageActions();

  const pages = useSelector(pagesModel.selectors.selectPages);

  const filteredPages = useFilteredPages({
    pages,
  });

  const { isGuest } = useTeamPermissions();

  return (
    <main className="relative flex flex-col flex-1 gap-y-2 min-w-0 md:m-0 mb-2 mr-2 overflow-y-auto">
      {!isGuest && (
        <div className="flex flex-nowrap flex-col gap-y-3 md:hidden p-6 bg-base-white border border-gray-200 rounded-xl">
          <h2 className="text-xl font-medium text-gray-900 truncate">
            What do you want to do?
          </h2>
          <div className="gap-3 grid max1330:grid-cols-[repeat(auto-fill,minmax(230px,1fr))] grid-cols-4">
            {QUICK_BUTTON_ITEMS.map((item) => (
              <WorkspaceQuickButton
                key={item.id}
                item={item}
                onClick={handleCreateNewPage}
              />
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-1 sm:p-5 p-6 bg-base-white border border-gray-200 md:border-none md:rounded-none rounded-xl">
        <div className="sm:px-1 sm:pt-1 min-w-0 flex flex-col flex-1 gap-y-6">
          <Helmet titleTemplate="All pages - Distribute"></Helmet>
          {currentPage && (
            <>
              <DeletePageModal
                isOpen={isDeletePageModalOpen}
                onClose={onDeletePageClose}
                page={currentPage}
              />
              <MoveToFolderModal
                isOpen={isMovePageToFolderModalOpen}
                onClose={onMoveToFolderClose}
                page={currentPage}
              />
            </>
          )}
          <WorkspaceShareModal
            isOpen={isSharePageModalOpen}
            onClose={handleSharePageClose}
          />
          {isGuest ? (
            <>
              <p className="flex-shrink-0 font-medium text-gray-900 truncate text-display-sm font-display">
                All pages
              </p>
              {pages.length > 0 && (
                <hr className="w-full bg-gray-200 md:hidden" />
              )}
            </>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-nowrap gap-x-3 items-center justify-between md:items-stretch md:gap-y-4 md:flex-col min-h-10">
                <p className="flex-shrink-0 font-medium text-gray-900 truncate text-display-sm font-display">
                  All pages
                </p>
                <WorkspaceFilters />
              </div>
              {pages.length > 0 && (
                <hr className="w-full bg-gray-200 md:hidden" />
              )}
            </div>
          )}
          <div className="flex-grow-1 flex flex-col">
            {filteredPages.length ? (
              view === 'grid' ? (
                <PagesGrid
                  pages={filteredPages}
                  onDeletePage={onDeletePageOpen}
                  onMoveToFolder={onMoveToFolderOpen}
                  onSharePage={handleSharePage}
                />
              ) : (
                <PagesList
                  pages={filteredPages}
                  onDeletePage={onDeletePageOpen}
                  onMoveToFolder={onMoveToFolderOpen}
                  onSharePage={handleSharePage}
                />
              )
            ) : (
              <EmptyAllPages />
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
